import { MoveValidationError } from "../../errors";
import { coordEq } from "../grid";
import { bfsReachable, shortestPath } from "../pathfinding";
import type { Coord, GameState, Unit } from "../types";
import { MAX_HP, UNIT_DEFS } from "../units";
import { activeVictoryCondition, countOwnedCities, ownsAllMonuments } from "../victory";

function checkObjectiveVictory(state: GameState): GameState["status"] | null {
  const condition = activeVictoryCondition(state);
  if (condition === "capture") {
    const target = state.captureCityTarget ?? 0;
    if (target <= 0) return null;
    if (countOwnedCities(state.tiles, "p1") >= target) return "victory";
    if (countOwnedCities(state.tiles, "p2") >= target) return "defeat";
    return null;
  }
  if (condition === "monuments") {
    if (ownsAllMonuments({ tiles: state.tiles, owner: "p1" })) return "victory";
    if (ownsAllMonuments({ tiles: state.tiles, owner: "p2" })) return "defeat";
  }
  return null;
}

function emptyMapOpeningFlags(state: GameState): {
  playerWipeGrace: boolean;
  enemyBuildPending: boolean;
} {
  const deployedUnitCount = state.initialUnitCount ?? state.units.length;
  const mapStartsEmpty = deployedUnitCount === 0;
  return {
    playerWipeGrace: mapStartsEmpty && state.currentDay <= 2,
    enemyBuildPending: mapStartsEmpty && state.currentDay <= 1,
  };
}

export function checkVictory(state: GameState): GameState["status"] {
  if (state.status === "victory" || state.status === "defeat") return state.status;

  const { playerWipeGrace, enemyBuildPending } = emptyMapOpeningFlags(state);
  const condition = activeVictoryCondition(state);
  const p1Alive = state.units.some((u) => u.owner === "p1");
  const p2Alive = state.units.some((u) => u.owner === "p2");

  if (condition === "rout") {
    if (!p1Alive && !playerWipeGrace) return "defeat";
    if (!p2Alive && p1Alive && !enemyBuildPending) return "victory";
    return "in_progress";
  }

  const objective = checkObjectiveVictory(state);
  if (objective) return objective;

  if (!p1Alive && !playerWipeGrace) return "defeat";
  return "in_progress";
}

export function pathDestination(path: Coord[], fallback: Coord): Coord {
  return path.length === 0 ? fallback : (path[path.length - 1] as Coord);
}

export function assertReachable({
  state,
  unit,
  dest,
  options,
}: {
  state: GameState;
  unit: Unit;
  dest: Coord;
  options?: { allowOccupiedDestination?: boolean };
}): void {
  if (coordEq(unit.pos, dest)) return;
  if (options?.allowOccupiedDestination) {
    const path = shortestPath({
      state,
      unit,
      dest,
      options: { allowOccupiedDestination: true },
    });
    if (!path) {
      throw new MoveValidationError(
        "path_unreachable",
        `Destination (${dest.x}, ${dest.y}) not reachable by ${unit.id}`,
      );
    }
    return;
  }
  const reachable = bfsReachable(state, unit);
  if (!reachable.some((c) => coordEq(c, dest))) {
    throw new MoveValidationError(
      "path_unreachable",
      `Destination (${dest.x}, ${dest.y}) not reachable by ${unit.id}`,
    );
  }
}

export function canJoinUnits(unit: Unit, target: Unit): boolean {
  if (unit.id === target.id) return false;
  if (unit.owner !== target.owner) return false;
  if (unit.kind !== target.kind) return false;
  if (unit.hasMoved) return false;
  const unitHp = Math.max(0, Math.min(unit.hp, MAX_HP));
  const targetHp = Math.max(0, Math.min(target.hp, MAX_HP));
  return unitHp < MAX_HP || targetHp < MAX_HP;
}

export function overflowGoldForJoin(kind: Unit["kind"], overflowHp: number): number {
  if (overflowHp <= 0) return 0;
  const def = UNIT_DEFS[kind];
  return Math.floor((overflowHp / MAX_HP) * def.cost);
}

export function combatRecordFields(
  source: Pick<Unit, "kills" | "captures">,
): Pick<Unit, "kills" | "captures"> {
  return {
    ...(source.kills !== undefined && source.kills > 0 ? { kills: source.kills } : {}),
    ...(source.captures !== undefined && source.captures > 0 ? { captures: source.captures } : {}),
  };
}

export function cargoFromPassenger(passenger: Unit): NonNullable<Unit["cargo"]> {
  return {
    kind: passenger.kind,
    hp: passenger.hp,
    ...combatRecordFields(passenger),
  };
}

export function mergeJoinedUnit({
  target,
  dest,
  facing,
  finalHp,
}: {
  target: Unit;
  dest: Coord;
  facing: Unit["facing"];
  finalHp: number;
}): Unit {
  return {
    ...target,
    pos: dest,
    hp: finalHp,
    hasMoved: true,
    hasAttacked: true,
    facing,
  };
}

export function assertGameInProgress(state: GameState): void {
  if (state.status !== "in_progress") {
    throw new MoveValidationError("game_not_in_progress", "Game is not in progress");
  }
}

export function findActingUnit(state: GameState, unitId: string): Unit {
  const unit = state.units.find((u) => u.id === unitId);
  if (!unit) throw new MoveValidationError("unit_not_found", `Unknown unit ${unitId}`);
  if (unit.owner !== state.currentPlayer) {
    throw new MoveValidationError(
      "wrong_owner",
      `Unit ${unit.id} is not owned by ${state.currentPlayer}`,
    );
  }
  if (unit.hasMoved && unit.hasAttacked) {
    throw new MoveValidationError("already_moved", `Unit ${unit.id} already acted this turn`);
  }
  return unit;
}

export function setUnit(
  state: GameState,
  { id, patch }: { id: string; patch: Partial<Unit> },
): GameState {
  return {
    ...state,
    units: state.units.map((u) => (u.id === id ? { ...u, ...patch } : u)),
  };
}
